'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsMobile } from './useIsMobile';
import { useIsMobileOverride } from '@jlg-website/editor/is-mobile';
import type { DesktopMenu, OpenMenu, TopDistance, Direction } from '@/components/pages/navbar/type';
import { toggleMenu } from '@/components/pages/navbar/menu-state';

const scrollYPositionTrigger = 130;
const navbarHiddenOffset = -200;
const bottomNavbarHiddenOffset = -100;
const LG_BREAKPOINT = 1024;
const ELEMENTS_TO_HIDE_NAVBOTTOM = ['form'];
const FORM_INPUT_SELECTOR = 'footer input[type="text"]';
const FORM_SCROLL_OFFSET = 24;
const FORM_SCROLL_DURATION_MS = 700;
const FORM_SCROLL_MAX_DURATION_MS = 2000;
const FORM_SCROLL_SETTLED_PX = 2;

const DATA_MENU_SELECTOR = '[data-menu]';

function topFromContainer(isMobile: boolean, serverInitialTop: TopDistance): number {
  if (typeof window === 'undefined')
    return isMobile ? serverInitialTop.mobile : serverInitialTop.desktop;
  return window.innerWidth >= LG_BREAKPOINT ? serverInitialTop.desktop : serverInitialTop.mobile;
}

function someElementVisible(elements: Element[]): boolean {
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  return elements.some((el) => {
    const rect = el.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < viewportHeight;
  });
}

function easeOutCubic(progress: number): number {
  return 1 - Math.pow(1 - progress, 3);
}

function scrollElementIntoViewWhileLayoutShifts(
  element: HTMLElement,
  focusWhenSettled: () => void
): () => void {
  const firstFrameAt = window.performance.now();
  let animationStartedAt = firstFrameAt;
  let animationStartedY = window.scrollY;
  let animationFrameId = 0;

  const scrollFrame = (now: number) => {
    const rect = element.getBoundingClientRect();
    const targetY = Math.max(0, window.scrollY + rect.top - FORM_SCROLL_OFFSET);
    const progress = Math.min(1, (now - animationStartedAt) / FORM_SCROLL_DURATION_MS);
    const nextY = animationStartedY + (targetY - animationStartedY) * easeOutCubic(progress);

    window.scrollTo({ top: nextY, behavior: 'auto' });

    const nextRect = element.getBoundingClientRect();
    const isSettled = Math.abs(nextRect.top - FORM_SCROLL_OFFSET) <= FORM_SCROLL_SETTLED_PX;
    const timedOut = now - firstFrameAt >= FORM_SCROLL_MAX_DURATION_MS;

    if ((progress === 1 && isSettled) || timedOut) {
      focusWhenSettled();
      return;
    }

    if (progress === 1) {
      animationStartedAt = now;
      animationStartedY = window.scrollY;
    }

    animationFrameId = window.requestAnimationFrame(scrollFrame);
  };

  animationFrameId = window.requestAnimationFrame(scrollFrame);
  return () => window.cancelAnimationFrame(animationFrameId);
}

export function useNavbar(serverInitialTop: TopDistance, minTop: TopDistance) {
  const isMobile = useIsMobile();
  const mobileOverride = useIsMobileOverride();
  const [initialTop, setInitialTop] = useState(() => topFromContainer(isMobile, serverInitialTop));
  const [isScrolled, setIsScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const [top, setTop] = useState<number | null>(null);
  const [transitionToTop, setTransitionToTop] = useState('none');
  const [bottom, setBottom] = useState(bottomNavbarHiddenOffset);

  const ticking = useRef(false);
  const lastScrollY = useRef(0);
  const currentTop = useRef(initialTop);
  const cancelFormScroll = useRef<(() => void) | null>(null);

  const scrollDirection = useRef<Direction>('down');
  const elementsToHideNavBottomRef = useRef<Element[]>([]);
  const overrideTop =
    mobileOverride === null
      ? null
      : mobileOverride
        ? serverInitialTop.mobile
        : serverInitialTop.desktop;
  const effectiveInitialTop = overrideTop ?? initialTop;
  const effectiveTop = overrideTop ?? top;
  const effectiveIsScrolled = mobileOverride === null ? isScrolled : false;

  const isMenuOpen = openMenu !== null;

  const toggleMenuOpen = useCallback(() => {
    setOpenMenu((prev) => (prev === 'mobile' ? null : 'mobile'));
  }, []);

  const openDesktopMenu = useCallback((menu: DesktopMenu) => {
    setOpenMenu((prev) => toggleMenu(prev, menu));
  }, []);

  const handleScroll = useCallback(() => {
    if (ticking.current) return;
    ticking.current = true;
    requestAnimationFrame(() => {
      const scrollY = window.scrollY;
      const delta = scrollY - lastScrollY.current;
      lastScrollY.current = scrollY;

      const direction = delta >= 0 ? 'down' : 'up';

      if (direction !== scrollDirection.current) {
        scrollDirection.current = direction;
        setTransitionToTop(direction === 'up' ? 'top 0.3s ease-out' : 'none');
      }

      if (isMobile) {
        setBottom((prev) => {
          const next =
            direction === 'down' && !someElementVisible(elementsToHideNavBottomRef.current)
              ? 0
              : bottomNavbarHiddenOffset;
          return prev !== next ? next : prev;
        });
      }

      const minVal = isMobile ? minTop.mobile : minTop.desktop;
      const newTop =
        scrollY <= effectiveInitialTop
          ? effectiveInitialTop
          : Math.min(minVal, Math.max(navbarHiddenOffset, currentTop.current - delta));
      if (newTop !== currentTop.current) {
        currentTop.current = newTop;
        setTop(newTop);
      }
      if (newTop < 0) {
        // Closing the navbar on scroll should dismiss desktop dropdowns, but never
        // the mobile drawer — that stays open until the user closes it explicitly.
        setOpenMenu((prev) => (prev === 'mobile' ? prev : null));
      }

      const scrolledState = scrollY > scrollYPositionTrigger;
      setIsScrolled((prev) => (prev !== scrolledState ? scrolledState : prev));

      ticking.current = false;
    });
  }, [effectiveInitialTop, minTop.desktop, minTop.mobile, isMobile]);

  useEffect(() => {
    if (!isMobile) return;

    const selector = ELEMENTS_TO_HIDE_NAVBOTTOM.join(',');
    const visibleElements = new Set<Element>();
    const observedElements = new Set<Element>();

    elementsToHideNavBottomRef.current = [];

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visibleElements.add(entry.target);
          else visibleElements.delete(entry.target);
        });
        if (visibleElements.size > 0) setBottom(bottomNavbarHiddenOffset);
      },
      { threshold: 0 }
    );

    const observe = (el: Element) => {
      if (observedElements.has(el)) return;
      observedElements.add(el);
      elementsToHideNavBottomRef.current.push(el);
      const rect = el.getBoundingClientRect();
      const vh = window.visualViewport?.height ?? window.innerHeight;
      if (rect.bottom > 0 && rect.top < vh) visibleElements.add(el);
      intersectionObserver.observe(el);
    };

    document.querySelectorAll(selector).forEach(observe);

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          const el = node as Element;
          if (el.matches(selector)) observe(el);
          el.querySelectorAll(selector).forEach(observe);
        });
      });
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [isMobile]);

  // Editor preview guard: mobileOverride is non-null only when this hook is rendered
  // inside the CMS PageEditor's PreviewFrame (IsMobileProvider context). Without this
  // guard, the effect reads the admin window's scroll position on mount and leaves
  // isScrolled=true, forcing the navbar into its scrolled/opaque style and creating
  // a visible gap below it in the editor canvas.
  useEffect(() => {
    if (mobileOverride !== null) return;

    lastScrollY.current = window.scrollY;
    currentTop.current = effectiveInitialTop;
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, effectiveInitialTop, mobileOverride]);

  useEffect(() => {
    const handleResize = () => {
      const newInitialTop =
        window.innerWidth >= LG_BREAKPOINT ? serverInitialTop.desktop : serverInitialTop.mobile;
      setInitialTop(newInitialTop);
      currentTop.current = newInitialTop;
      setTop(newInitialTop);
    };

    window.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
    };
  }, [serverInitialTop.desktop, serverInitialTop.mobile]);

  useEffect(() => {
    if (isMobile) document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      if (isMobile) document.body.style.overflow = '';
    };
  }, [isMenuOpen, isMobile]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target?.closest?.(DATA_MENU_SELECTOR)) {
        setOpenMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  const scrollToForm = useCallback(() => {
    const element = document.querySelector(FORM_INPUT_SELECTOR) as HTMLInputElement | null;
    if (!element) return;

    cancelFormScroll.current?.();
    cancelFormScroll.current = scrollElementIntoViewWhileLayoutShifts(element, () => {
      cancelFormScroll.current = null;
      element.focus({ preventScroll: true });
    });
  }, []);

  useEffect(() => {
    return () => {
      cancelFormScroll.current?.();
    };
  }, []);

  return {
    isMobile,
    isScrolled: effectiveIsScrolled,
    isMenuOpen,
    openMenu,
    toggleMenuOpen,
    openDesktopMenu,
    top: effectiveTop || undefined,
    transitionToTop,
    bottom,
    scrollToForm,
  };
}
